import { Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'

interface CustomHeatmapGridProps {
	data: number[][]
	xLabels: string[]
	yLabels: string[]
	maxValue: number
	getCellBackgroundColor: (value: number, maxValue: number) => string
	renderCellContent?: (value: number) => React.ReactNode
	cellHeight?: string | number
	yLabelWidth?: string | number
}

export const CustomHeatmapGrid: React.FC<CustomHeatmapGridProps> = ({
	data,
	xLabels,
	yLabels,
	maxValue,
	getCellBackgroundColor,
	renderCellContent,
	cellHeight = '35px',
	yLabelWidth = '60px',
}) => {
	const tokens = ddsTokens[DDSTheme.light]

	if (!data || data.length === 0) {
		return <Text>No data to display.</Text>
	}

	const getDataCellStyles = (value: number) => ({
		height: cellHeight,
		background: getCellBackgroundColor(value, maxValue),
		fontSize: '10px',
		color:
			value > maxValue * 0.5
				? tokens.primitive.light.white[100]
				: tokens.primitive.light.gray[800],
		borderRadius: '3px',
		textAlign: 'center' as const,
		padding: '0',
		minWidth: '35px',
	})

	const xLabelHeaderStyles = {
		height: 'auto',
		minHeight: '24px',
		fontSize: 'xs',
		color: tokens.primitive.light.gray[600],
		textAlign: 'center' as const,
		padding: '4px 2px',
		border: 'none',
		fontWeight: 'normal',
	}

	const yLabelHeaderStyles = {
		width: yLabelWidth,
		height: cellHeight,
		textAlign: 'center' as const,
		fontSize: 'xs',
		fontWeight: 'normal',
		padding: '0 4px',
		border: 'none',
		verticalAlign: 'middle',
		position: 'sticky' as const,
		left: 0,
		backgroundColor: tokens.primitive.light.white[100],
		zIndex: 1,
	}

	return (
		<Table
			variant="unstyled"
			width="100%"
			style={{
				borderCollapse: 'separate',
				borderSpacing: '2px',
			}}
		>
			<Thead>
				<Tr>
					<Th
						style={{
							...yLabelHeaderStyles,
							width: yLabelWidth,
							border: 'none',
							zIndex: 2, // Higher z-index for the corner cell
						}}
					></Th>
					{xLabels.map((label, index) => (
						<Th key={`x-label-${index}`} style={xLabelHeaderStyles}>
							{label}
						</Th>
					))}
				</Tr>
			</Thead>
			<Tbody>
				{yLabels.map((yLabel, rowIndex) => (
					<Tr key={`row-${rowIndex}`}>
						<Th scope="row" style={yLabelHeaderStyles}>
							{yLabel}
						</Th>
						{data[rowIndex]?.map((cellValue, colIndex) => (
							<Td
								key={`cell-${rowIndex}-${colIndex}`}
								style={getDataCellStyles(cellValue)}
								title={`${cellValue} meetings`}
							>
								{renderCellContent
									? renderCellContent(cellValue)
									: cellValue > 0
										? cellValue
										: ''}
							</Td>
						))}
					</Tr>
				))}
			</Tbody>
		</Table>
	)
}

export default CustomHeatmapGrid
